import { TicketQueryDto } from '@packages/database/dtos';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
// import type { TicketQueryDto } from '@packages/database/types';

// Only include keys that are actual API query params
type TicketFilterKeys = keyof TicketQueryDto;

const FILTER_KEYS: TicketFilterKeys[] = [
  'q',
  'status',
  'priority',
  'projectId',
  'crewId',
  'createdOn',
  'category',
  'assignmentStatus',
  'assignedTo',
  'scope',
  'page',
  'limit',
];

/**
 * Reads ticket filters from URL search params and provides
 * typed setters. Empty/default values are omitted from the URL
 * and from the API request — the backend handles defaults.
 */
export const useTicketFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo((): TicketQueryDto => {
    const parsed: Record<string, string | number | undefined> = {};

    for (const key of FILTER_KEYS) {
      const value = searchParams.get(key);

      if (!value || value === 'all') continue;

      if (key === 'page' || key === 'limit') {
        parsed[key] = Number(value) || undefined;
      } else {
        parsed[key] = value;
      }
    }

    return {
      page: (parsed.page as number) || 1,
      limit: (parsed.limit as number) || 20,
      ...parsed,
    } as TicketQueryDto;
  }, [searchParams]);

  const setFilters = useCallback(
    (newFilters: Partial<TicketQueryDto>) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);

          for (const [key, value] of Object.entries(newFilters)) {
            if (
              value === undefined ||
              value === null ||
              value === '' ||
              value === 'all'
            ) {
              updated.delete(key);
            } else {
              updated.set(key, String(value));
            }
          }

          // Always reset to page 1 when non-pagination filters change
          const isOnlyPageChange =
            Object.keys(newFilters).length === 1 && 'page' in newFilters;
          if (!isOnlyPageChange) {
            updated.set('page', '1');
          }

          return updated;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setPage = useCallback(
    (page: number) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          updated.set('page', String(page));
          return updated;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  // Derive active filter count (for showing a badge on the filter button)
  const activeFilterCount = useMemo(() => {
    let count = 0;
    for (const key of FILTER_KEYS) {
      if (key === 'page' || key === 'limit') continue;
      const value = searchParams.get(key);
      if (value && value !== 'all') count++;
    }
    return count;
  }, [searchParams]);

  return {
    filters,
    setFilters,
    setPage,
    clearFilters,
    activeFilterCount,
  };
};
